/**
 * AppSync Events WebSocket client (singleton) for live game channel.
 * No GraphQL — raw Events protocol.
 *
 * Connection stays open only while cached games are live or near kickoff.
 * Closed when the slate is quiet — forceListen must not keep a socket open.
 *
 * Recovery invariant (live slate only): fetch session caches → resume handlers → reconnect.
 * Never reconnect on a stale cache; never leave a live slate without a retry loop.
 *
 * Recover only on real failure signals (hard close, soft-dead silence past ka interval,
 * hung connect, back online) — not on a healthy quiet socket.
 */

import {
  applyLiveEventToHomeCache,
  applyLiveEventToRoundsCache,
  betsByLeagueStore,
  collectGamesFromHomeCache,
  collectGamesFromRoundsCache,
  homeByLeagueStore
} from '../utils/leaguePageCache';
import { shouldListenForLiveGames } from '../utils/liveMatchClock';
import { refetchAllLivePageCaches } from './refetchLivePageCaches';

const DEFAULT_CHANNEL = 'live/games';
const EVALUATE_INTERVAL_MS = 15_000;
/** Past AppSync ~60s ka so a healthy quiet socket is not false-stale. */
const STALE_MESSAGE_MS = 75_000;
const CONNECTING_HUNG_MS = 20_000;
const UNEXPECTED_RECONNECT_DELAY_MS = 2_000;
const RECOVER_COOLDOWN_MS = 5_000;

function base64UrlEncode(obj) {
  const json = JSON.stringify(obj);
  const b64 =
    typeof btoa === 'function'
      ? btoa(json)
      : Buffer.from(json, 'utf8').toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function readConfig() {
  const httpHost = (process.env.REACT_APP_APPSYNC_EVENTS_HTTP_HOST || '').trim();
  const realtimeHost = (process.env.REACT_APP_APPSYNC_EVENTS_REALTIME_HOST || '').trim();
  const apiKey = (process.env.REACT_APP_APPSYNC_EVENTS_API_KEY || '').trim();
  const channel = (process.env.REACT_APP_APPSYNC_EVENTS_CHANNEL || DEFAULT_CHANNEL).trim();
  return { httpHost, realtimeHost, apiKey, channel };
}

export function isLiveGameSyncConfigured() {
  const { httpHost, realtimeHost, apiKey } = readConfig();
  return Boolean(httpHost && realtimeHost && apiKey);
}

/**
 * @typedef {{ next: (event: object) => void, error?: (err: Error) => void }} LiveGameSyncHandlers
 */

let shared = {
  socket: null,
  subscriptionId: null,
  handlers: new Set(),
  /** @type {Set<() => void | Promise<void>>} */
  resumeHandlers: new Set(),
  forceListenTokens: new Set(),
  connecting: null,
  intentionalClose: false,
  evaluateTimer: null,
  reconnectTimer: null,
  recovering: null,
  lastRecoverStartedAtMs: 0,
  lastMessageAtMs: 0,
  connectStartedAtMs: 0,
  onlineBound: false
};

function collectCachedGames() {
  return [
    ...collectGamesFromHomeCache(homeByLeagueStore.get()),
    ...collectGamesFromRoundsCache(betsByLeagueStore.get())
  ];
}

export function wantsLiveGameSyncConnection() {
  // Socket only while live / near-kickoff games exist in session caches.
  // forceListen must not keep a quiet WebSocket open.
  return shouldListenForLiveGames(collectCachedGames());
}

/** Live / near-kickoff slate — full recover (fetch+reconnect) only makes sense then. */
function hasLiveOrUpcomingGames() {
  return shouldListenForLiveGames(collectCachedGames());
}

function touchLastMessage() {
  shared.lastMessageAtMs = Date.now();
}

function stopEvaluateTimer() {
  if (shared.evaluateTimer != null) {
    clearInterval(shared.evaluateTimer);
    shared.evaluateTimer = null;
  }
}

function onOnline() {
  if (shared.intentionalClose || !hasLiveOrUpcomingGames()) return;
  recoverLiveGameSync({ forceReconnect: false }).catch((err) => {
    console.warn('liveGameSync recover on online failed', err);
  });
}

function ensureOnlineListener() {
  if (shared.onlineBound || typeof window === 'undefined') return;
  shared.onlineBound = true;
  window.addEventListener('online', onOnline);
}

function removeOnlineListener() {
  if (!shared.onlineBound || typeof window === 'undefined') return;
  window.removeEventListener('online', onOnline);
  shared.onlineBound = false;
}

/**
 * Drop the current socket without treating it as intentional disconnect,
 * and without scheduling a nested unexpected-reconnect from onclose.
 */
function abortSocketForRecover() {
  const ws = shared.socket;
  shared.socket = null;
  shared.subscriptionId = null;
  shared.connecting = null;
  shared.connectStartedAtMs = 0;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    try {
      ws.close();
    } catch {
      /* ignore */
    }
  }
}

function isHealthyOpenSocket(now = Date.now()) {
  const ws = shared.socket;
  if (!ws || ws.readyState !== WebSocket.OPEN) return false;
  const last = shared.lastMessageAtMs || shared.connectStartedAtMs || 0;
  return now - last < STALE_MESSAGE_MS;
}

/**
 * Fetch all session live caches, run mounted page resume handlers, then connect.
 * @param {{ forceReconnect?: boolean }} [opts]
 */
async function recoverLiveGameSync(opts = {}) {
  const forceReconnect = Boolean(opts.forceReconnect);
  // Fetch+resume is for live/near-kickoff slates only.
  if (shared.intentionalClose || !hasLiveOrUpcomingGames()) return;
  if (shared.recovering) return shared.recovering;

  const now = Date.now();
  if (now - shared.lastRecoverStartedAtMs < RECOVER_COOLDOWN_MS) return;
  shared.lastRecoverStartedAtMs = now;

  shared.recovering = (async () => {
    clearReconnectTimer();
    const needsReconnect = forceReconnect || !isHealthyOpenSocket();
    if (needsReconnect) {
      abortSocketForRecover();
    }
    try {
      await refetchAllLivePageCaches();
    } catch (err) {
      console.warn('liveGameSync cache refetch during recover failed', err);
    }
    await notifyResumeHandlers();
    if (!shared.intentionalClose && wantsLiveGameSyncConnection()) {
      await connectLiveGameSync();
    }
  })()
    .catch((err) => {
      console.warn('liveGameSync recover failed', err);
    })
    .finally(() => {
      shared.recovering = null;
    });

  return shared.recovering;
}

function evaluateWatchdog() {
  if (!isLiveGameSyncConfigured()) return;

  if (!wantsLiveGameSyncConnection()) {
    disconnectLiveGameSync();
    return;
  }

  ensureListenInfrastructure();

  const ws = shared.socket;
  const now = Date.now();

  if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
    recoverLiveGameSync({ forceReconnect: true }).catch(() => {});
    return;
  }

  if (ws.readyState === WebSocket.CONNECTING) {
    const abandoned = shared.connecting == null;
    const hung =
      shared.connectStartedAtMs > 0 &&
      now - shared.connectStartedAtMs >= CONNECTING_HUNG_MS;
    if (abandoned || hung) {
      recoverLiveGameSync({ forceReconnect: true }).catch(() => {});
    }
    return;
  }

  if (ws.readyState === WebSocket.OPEN) {
    const last = shared.lastMessageAtMs || shared.connectStartedAtMs || 0;
    if (now - last >= STALE_MESSAGE_MS) {
      recoverLiveGameSync({ forceReconnect: true }).catch(() => {});
    }
  }
}

function ensureEvaluateTimer() {
  if (shared.evaluateTimer != null) return;
  shared.evaluateTimer = setInterval(() => {
    evaluateWatchdog();
  }, EVALUATE_INTERVAL_MS);
}

function ensureListenInfrastructure() {
  ensureEvaluateTimer();
  // Online recover only while a live/near-kickoff game exists.
  if (hasLiveOrUpcomingGames()) {
    ensureOnlineListener();
  } else {
    removeOnlineListener();
  }
}

function notifyNext(payload) {
  const snapshot = payload?.game;
  const gameId = payload?.gameId || snapshot?.gameId;
  if (gameId && snapshot && typeof snapshot === 'object') {
    homeByLeagueStore.set(
      applyLiveEventToHomeCache(homeByLeagueStore.get(), gameId, snapshot)
    );
    betsByLeagueStore.set(
      applyLiveEventToRoundsCache(betsByLeagueStore.get(), gameId, snapshot)
    );
  }

  for (const h of shared.handlers) {
    try {
      h.next(payload);
    } catch (err) {
      console.warn('liveGameSync handler error', err);
    }
  }

  // GAME_ENDED / status changes may mean we should drop the socket.
  syncLiveGameSyncConnection().catch(() => {});
}

function notifyError(err) {
  for (const h of shared.handlers) {
    try {
      h.error?.(err);
    } catch {
      /* ignore */
    }
  }
}

async function notifyResumeHandlers() {
  const handlers = [...shared.resumeHandlers];
  await Promise.all(
    handlers.map(async (handler) => {
      try {
        await handler();
      } catch (err) {
        console.warn('liveGameSync resume handler failed', err);
      }
    })
  );
}

function clearReconnectTimer() {
  if (shared.reconnectTimer != null) {
    clearTimeout(shared.reconnectTimer);
    shared.reconnectTimer = null;
  }
}

/**
 * After an unexpected socket drop: full recover when live games exist,
 * otherwise no-op (quiet slate should not hold a socket).
 */
function scheduleUnexpectedReconnect() {
  clearReconnectTimer();
  shared.reconnectTimer = setTimeout(() => {
    shared.reconnectTimer = null;
    if (hasLiveOrUpcomingGames()) {
      recoverLiveGameSync({ forceReconnect: true }).catch((err) => {
        console.warn('liveGameSync reconnect after close failed', err);
      });
    }
  }, UNEXPECTED_RECONNECT_DELAY_MS);
}

function parseIncomingMessage(raw) {
  let msg;
  try {
    msg = JSON.parse(raw);
  } catch {
    return;
  }

  if (msg.type !== 'data') return;

  const payloads = [];
  if (Array.isArray(msg.event)) {
    payloads.push(...msg.event);
  } else if (msg.event != null) {
    payloads.push(msg.event);
  }
  if (Array.isArray(msg.events)) {
    payloads.push(...msg.events);
  }

  for (const eventRaw of payloads) {
    try {
      const parsed = typeof eventRaw === 'string' ? JSON.parse(eventRaw) : eventRaw;
      // Double-encoded string payloads: "\"{...}\"" → string → object
      const event =
        typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
      if (event && typeof event === 'object') notifyNext(event);
    } catch {
      /* ignore malformed */
    }
  }
}

/**
 * Ensure a single shared WebSocket subscription is open.
 * @returns {Promise<void>}
 */
export async function connectLiveGameSync() {
  if (!isLiveGameSyncConfigured()) return;
  if (shared.socket && shared.socket.readyState === WebSocket.OPEN) {
    ensureListenInfrastructure();
    return;
  }
  if (shared.connecting) return shared.connecting;

  const { httpHost, realtimeHost, apiKey, channel } = readConfig();
  const authorization = { 'x-api-key': apiKey, host: httpHost };
  const authProtocol = `header-${base64UrlEncode(authorization)}`;

  shared.intentionalClose = false;
  shared.connectStartedAtMs = Date.now();
  ensureListenInfrastructure();

  shared.connecting = new Promise((resolve, reject) => {
    let settled = false;
    const ws = new WebSocket(`wss://${realtimeHost}/event/realtime`, [
      'aws-appsync-event-ws',
      authProtocol
    ]);
    shared.socket = ws;

    const fail = (err) => {
      if (settled) return;
      settled = true;
      shared.connecting = null;
      ensureListenInfrastructure();
      reject(err);
    };

    ws.onopen = () => {
      touchLastMessage();
      ws.send(JSON.stringify({ type: 'connection_init' }));
    };

    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }

      touchLastMessage();

      if (msg.type === 'connection_ack') {
        const id =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `sub-${Date.now()}`;
        shared.subscriptionId = id;
        ws.send(
          JSON.stringify({
            type: 'subscribe',
            id,
            channel: channel.startsWith('/') ? channel : `/${channel}`,
            authorization
          })
        );
        if (!settled) {
          settled = true;
          shared.connecting = null;
          ensureListenInfrastructure();
          resolve();
        }
        return;
      }

      if (msg.type === 'subscribe_success' || msg.type === 'ka' || msg.type === 'keepalive') {
        return;
      }

      parseIncomingMessage(event.data);
    };

    ws.onerror = () => {
      const err = new Error('AppSync Events WebSocket error');
      notifyError(err);
      fail(err);
    };

    ws.onclose = () => {
      // Ignore stale closes from a socket we already replaced / aborted for recover.
      if (shared.socket !== ws) return;
      shared.socket = null;
      shared.subscriptionId = null;
      shared.connecting = null;
      if (!shared.intentionalClose && wantsLiveGameSyncConnection()) {
        scheduleUnexpectedReconnect();
      } else if (!wantsLiveGameSyncConnection()) {
        stopEvaluateTimer();
      }
    };
  });

  return shared.connecting;
}

export function disconnectLiveGameSync() {
  shared.intentionalClose = true;
  clearReconnectTimer();
  stopEvaluateTimer();
  removeOnlineListener();
  shared.recovering = null;
  shared.lastRecoverStartedAtMs = 0;
  shared.lastMessageAtMs = 0;
  shared.connectStartedAtMs = 0;
  const ws = shared.socket;
  shared.socket = null;
  shared.subscriptionId = null;
  shared.connecting = null;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    try {
      ws.close();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Connect or disconnect solely from live-game need (caches + forceListen).
 * @returns {Promise<void>}
 */
export async function syncLiveGameSyncConnection() {
  if (!isLiveGameSyncConfigured()) return;
  if (wantsLiveGameSyncConnection()) {
    ensureListenInfrastructure();
    await connectLiveGameSync();
    return;
  }
  disconnectLiveGameSync();
}

/** @param {object} token opaque retain token from the caller */
export function retainLiveGameSyncForceListen(token) {
  shared.forceListenTokens.add(token);
  if (wantsLiveGameSyncConnection()) {
    ensureListenInfrastructure();
  }
}

/** @param {object} token */
export function releaseLiveGameSyncForceListen(token) {
  shared.forceListenTokens.delete(token);
}

/**
 * Subscribe to live game events. Returns unsubscribe.
 * Unsubscribing does not close the socket — only syncLiveGameSyncConnection does.
 * @param {LiveGameSyncHandlers} handlers
 */
export function subscribeLiveGameEvents(handlers) {
  shared.handlers.add(handlers);
  return () => {
    shared.handlers.delete(handlers);
  };
}

/**
 * Register a handler that runs before reconnecting after an unexpected socket drop
 * (fetch fresh games, then connect). Also used by tab-visibility resume callers.
 * @param {() => void | Promise<void>} handler
 * @returns {() => void} unsubscribe
 */
export function resumeLiveGameSync(handler) {
  shared.resumeHandlers.add(handler);
  return () => {
    shared.resumeHandlers.delete(handler);
  };
}

/** @internal test helper */
export function _resetLiveGameSyncForTests() {
  disconnectLiveGameSync();
  shared.handlers.clear();
  shared.resumeHandlers.clear();
  shared.forceListenTokens.clear();
  shared.intentionalClose = false;
  shared.recovering = null;
  shared.lastRecoverStartedAtMs = 0;
  shared.lastMessageAtMs = 0;
  shared.connectStartedAtMs = 0;
}
